import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useUserRole } from '@/hooks/useUserRole';
import { availableLanguages, isAvailable, LANGUAGES, type LanguageCode } from '@/lib/i18n/registry';
import { resolveLanguage } from '@/lib/i18n/resolve';
import { isReviewEnvironment, readStoredLanguage, writeStoredLanguage } from '@/lib/i18n/environment';

/** Review languages: preview hosts, or signed-in reviewer/admin accounts on any host. */
function useReviewAccess() {
  const { isReviewer } = useUserRole();
  return isReviewEnvironment() || isReviewer;
}

/**
 * Keeps i18next in sync with the signed-in user's saved preference.
 * Language never touches currency, date format, time zone or any financial
 * value — those are separate settings.
 */
export function LanguageSync() {
  const { user } = useAuth();
  const { i18n } = useTranslation();
  const reviewEnv = useReviewAccess();

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('preferred_language')
        .eq('user_id', user.id)
        .maybeSingle();
      if (cancelled || error || !data) return;
      const saved = (data as { preferred_language?: string | null } | null)?.preferred_language ?? null;
      const stored = readStoredLanguage();
      // Carry an anonymous choice only onto a profile with no preference.
      // English is an explicit preference, just like Arabic or Spanish.
      if (saved === null && isAvailable(stored, reviewEnv)) {
        await supabase.from('profiles').update({ preferred_language: stored }).eq('user_id', user.id).is('preferred_language', null);
        if (cancelled) return;
      }
      const { language } = resolveLanguage({
        profileLanguage: saved,
        storedLanguage: stored,
        browserLanguages: navigator.languages,
        reviewEnv,
      });
      if (i18n.language !== language) void i18n.changeLanguage(language);
    })();
    return () => {
      cancelled = true;
    };
  }, [user, i18n, reviewEnv]);

  return null;
}

export function useLanguage() {
  const { user } = useAuth();
  const { i18n } = useTranslation();
  const reviewEnv = useReviewAccess();
  const options = availableLanguages(reviewEnv).map((c) => LANGUAGES[c]);
  const current = (i18n.language in LANGUAGES ? i18n.language : 'en') as LanguageCode;

  /** Changes language in place: same route, same unsaved inputs. */
  const setLanguage = useCallback(
    async (code: LanguageCode) => {
      if (!isAvailable(code, reviewEnv)) return { error: new Error('unavailable') };
      writeStoredLanguage(code);
      await i18n.changeLanguage(code);
      if (!user) return { error: null };
      const { error } = await supabase
        .from('profiles')
        .update({ preferred_language: code, updated_at: new Date().toISOString() })
        .eq('user_id', user.id);
      return { error };
    },
    [i18n, user, reviewEnv],
  );

  return { current, options, setLanguage, reviewEnv };
}
